'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import Link from 'next/link';
import { Edit, EyeOff, Calendar, Bookmark, BookmarkCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Markdown } from '../Markdown';
import { LazyImage } from '../LazyImage';
import { ImageGallery, type ImageGalleryDisplayMode } from '@/components/ui/image-gallery';
import { EmbeddedMap } from '@/components/ui/embedded-map';
import { CONTENT_TYPE_CONFIG, CONTENT_STATUS_OPTIONS } from '@sizzlenz/shared';
import type { PlaybookDetail } from '@sizzlenz/shared';
import type { ApiContent } from '@/lib/api/types';
import { getContentDisplayDate } from '@/lib/utils';
import { ActivityFeed, ActivityOperations } from '@/components/activities';
import { EnquiryForm, type EnquiryTypeOption } from '@/components/enquiries';
import { ContactInfo } from '@/components/ui/contact-info';
import { ClickableTags } from '@/components/ui/clickable-tags';
import { HighlightsDisplay } from './HighlightsDisplay';
import type { Highlight } from './HighlightsEditor';
import { MarketingDetailsSnapshot } from './MarketingDetailsSnapshot';
import type { MarketingDetailsValue } from './MarketingDetailsForm';
import { useSession } from '@/lib/auth-client';
import { canAccessInitiativeContact } from '@/lib/utils/trade';
import { PlaybookDetailView } from '@/components/spark/PlaybookDetailView';
import { WorkflowDetailView } from '@/components/workflows/WorkflowDetailView';
import type { Workflow } from '@sizzlenz/shared';
import { FollowButton } from '../FollowButton';
interface ContentDetailViewProps {
  content: ApiContent;
  showMap?: boolean;
  /** Show the admin Edit button. Defaults to true. */
  showEditButton?: boolean;
  galleryDisplayMode?: ImageGalleryDisplayMode;
  defaultEnquiryType?: string;
  /**
   * Cover image display config. Overrides any config on `content.data.coverImage`.
   * Use this to force banner behaviour for a specific surface (e.g. always
   * 'contain' in a preview). Omit to use per-content config, then the defaults.
   */
  coverImageConfig?: CoverImageConfig;
  /**
   * When true, Demand Details chips link through to the Inspiration Feed
   * pre-filtered by their value. Defaults to true. Set false for previews
   * (e.g. the ContentForm preview sheet) where navigation isn't wanted.
   */
  linkDemandTags?: boolean;
  /**
   * When true, render a Save/Follow button in its own section below the main
   * content (before comments), in addition to the top actions bar. Only shown
   * when follow status is available on the content (`content.following`).
   * Defaults to false.
   */
  showFollowAfterContent?: boolean;
}
function formatDate(d: string | null | undefined) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}
function getContentEditUrl(content: ApiContent): string {
  if (content.ownerType === 'entity' && content.ownerId) {
    return `/entities/${content.ownerId}/admin/content/${content.id}/edit`;
  }
  return `/admin/content/${content.id}/edit`;
}

/**
 * Per-content cover image display config, stored on `content.data.coverImage`.
 *
 * All fields are optional and fall back to the recommended defaults
 * (16:9 aspect ratio, cover fit, center position). See COVER_IMAGE_DEFAULTS.
 *
 * @example
 * // content.data
 * {
 *   coverImage: {
 *     aspectRatio: '4 / 3',      // taller banner, crops less on portrait images
 *     objectFit: 'contain',      // show the whole image, letterboxed
 *     objectPosition: 'top',     // when cropping, protect the top (good for faces)
 *   }
 * }
 */
interface CoverImageConfig {
  /**
   * CSS aspect-ratio for the banner (e.g. '16 / 9', '4 / 3', '21 / 9').
   * A wider ratio (21 / 9) makes a slimmer banner; a taller ratio (4 / 3)
   * crops portrait images less. Defaults to '16 / 9'.
   *
   * To show the whole image without cropping, keep a ratio here and set
   * objectFit to 'contain' (the image is letterboxed within the ratio box).
   */
  aspectRatio?: string;
  /**
   * How the image fills the banner box.
   * - 'cover' (default): fill edge-to-edge, cropping overflow. Consistent banner.
   * - 'contain': show the whole image, letterboxing against the background.
   * Defaults to 'cover'.
   */
  objectFit?: 'cover' | 'contain';
  /**
   * Which part of the image is kept when cropping with 'cover'.
   * Any CSS object-position value: 'center', 'top', 'bottom', 'left', 'right',
   * or a precise focal point like '50% 25%'. Ignored when objectFit is 'contain'.
   * Defaults to 'center'.
   */
  objectPosition?: string;
}
const COVER_IMAGE_DEFAULTS = {
  aspectRatio: '16 / 9',
  objectFit: 'cover',
  objectPosition: 'center'
} as const satisfies Required<CoverImageConfig>;

/**
 * Cover image banner with configurable, responsive display.
 *
 * Default behaviour: a fixed 16:9 aspect-ratio container using object-cover
 * centered, marked as priority (LCP). This crops the same way at every
 * viewport, so the framing is predictable across screen sizes rather than
 * shifting per breakpoint.
 *
 * Configure per-content via `content.data.coverImage` (see CoverImageConfig).
 */
function CoverImageBanner(t0) {
  const $ = _c(7);
  const {
    src,
    alt,
    config
  } = t0;
  const aspectRatio = config?.aspectRatio ?? COVER_IMAGE_DEFAULTS.aspectRatio;
  const objectFit = config?.objectFit ?? COVER_IMAGE_DEFAULTS.objectFit;
  const objectPosition = config?.objectPosition ?? COVER_IMAGE_DEFAULTS.objectPosition;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== alt || $[2] !== aspectRatio || $[3] !== objectFit || $[4] !== objectPosition || $[5] !== src) {
    t2 = <div className="relative w-full bg-gray-100"><LazyImage src={src} alt={alt} aspectRatio={aspectRatio} objectFit={objectFit} objectPosition={objectPosition} priority={true} className="w-full" fallback={t1} /></div>;
    $[1] = alt;
    $[2] = aspectRatio;
    $[3] = objectFit;
    $[4] = objectPosition;
    $[5] = src;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

/**
 * Renders the main content body based on content type.
 * For types with a dedicated renderer (e.g. playbook), uses that renderer.
 * Falls back to Markdown rendering for all other types.
 */
function ContentBody({
  content
}: {
  content: ApiContent;
}) {
  const workflowFromData = (content.data as any)?.workflow as Workflow | undefined;
  const hasWorkflow = workflowFromData && workflowFromData.version && workflowFromData.components;
  if (content.type === 'playbook' && content.content) {
    try {
      const cleaned = content.content.replace(/\n/g, ' ').replace(/\r/g, '');
      const playbookData = JSON.parse(cleaned) as PlaybookDetail;
      const playbook: PlaybookDetail = {
        ...playbookData,
        id: playbookData.id || content.slug || String(content.id),
        title: content.title || playbookData.title || '',
        subtitle: content.blurb || playbookData.subtitle || '',
        description: playbookData.description || ''
      };
      return <PlaybookDetailView playbook={playbook} embedded />;
    } catch {
      // Fall through to markdown if JSON parsing fails
    }
  }
  return <>
      {/* Show main content body (markdown) when present */}
      {content.content && <section className="prose prose-indigo max-w-none">
          <Markdown>{content.content}</Markdown>
        </section>}

      {/* Workflow section — rendered below content body when present */}
      {hasWorkflow && <div className={content.content ? 'mt-8' : undefined}>
          {workflowFromData.description && <section className="prose prose-indigo max-w-none mb-6">
              <Markdown>{workflowFromData.description}</Markdown>
            </section>}
          <WorkflowDetailView workflow={workflowFromData} embedded showComponentSources variant="infographic" collapseConfig={{
        defaultCollapsed: true
      }} phaseLabelPosition="above" showPhaseLabel trackingObjectId={content.id} trackingObjectType="content"
      // forceMobileLayout
      />
        </div>}
    </>;
}
export function ContentDetailView(t0) {
  const $ = _c(142);
  const {
    content,
    showMap: t1,
    showEditButton: t2,
    galleryDisplayMode: t3,
    defaultEnquiryType,
    coverImageConfig,
    linkDemandTags: t4,
    showFollowAfterContent: t5
  } = t0;
  const showMap = t1 === undefined ? false : t1;
  const showEditButton = t2 === undefined ? true : t2;
  const galleryDisplayMode = t3 === undefined ? "grid" : t3;
  const linkDemandTags = t4 === undefined ? true : t4;
  const showFollowAfterContent = t5 === undefined ? true : t5;
  const typeConfig = CONTENT_TYPE_CONFIG[content.type as keyof typeof CONTENT_TYPE_CONFIG];
  let t6;
  if ($[0] !== content.status) {
    t6 = CONTENT_STATUS_OPTIONS.find(o => o.value === content.status);
    $[0] = content.status;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const statusOption = t6;
  const isLive = content.status === "published";
  const coverImage = content.image || null;
  const resolvedCoverImageConfig = coverImageConfig ?? (content.data as any)?.coverImage as CoverImageConfig | undefined;
  let t7;
  if ($[2] !== content.data) {
    t7 = (content.data as any)?.gallery?.images as string[] || [];
    $[2] = content.data;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const galleryImages = t7;
  let t8;
  if ($[4] !== galleryImages) {
    t8 = galleryImages.map(_temp);
    $[4] = galleryImages;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const galleryItems = t8;
  const addressData = (content.data as any)?.address_1 as {
    formatted_address?: string;
    geometry?: {
      location?: {
        lat: number;
        lng: number;
      };
    };
  } | null | undefined;
  const hasAddress = addressData?.geometry?.location?.lat != null && addressData?.geometry?.location?.lng != null;
  const itemDate = getContentDisplayDate(content);
  const allowReactions = (content.data as any)?.allowReactions !== false;
  const allowComments = (content.data as any)?.allowComments !== false;
  let t9;
  bb0: {
    let t10;
    if ($[6] !== content.data) {
      t10 = (content.data as any)?.contact || {};
      $[6] = content.data;
      $[7] = t10;
    } else {
      t10 = $[7];
    }
    const contact = t10;
    const resolved = contact.resolved as {
      name?: string;
      email?: string;
      phone?: string;
      website?: string;
    } | undefined;
    if (!resolved) {
      t9 = contact;
      break bb0;
    }
    let t11;
    if ($[8] !== contact || $[9] !== resolved.email || $[10] !== resolved.name || $[11] !== resolved.phone || $[12] !== resolved.website) {
      t11 = {
        ...contact,
        name: resolved.name,
        email: resolved.email,
        phone: resolved.phone,
        website: resolved.website
      };
      $[8] = contact;
      $[9] = resolved.email;
      $[10] = resolved.name;
      $[11] = resolved.phone;
      $[12] = resolved.website;
      $[13] = t11;
    } else {
      t11 = $[13];
    }
    t9 = t11;
  }
  const contactDetails = t9;
  const {
    data: session
  } = useSession();
  const hasContactEmail = !!contactDetails.email;
  const hideContactDetails = contactDetails.hideDetails === true;
  const contactVisibility = contactDetails.visibility;
  const t10 = session?.user?.isAdmin;
  const t11 = (session?.user?.data as any)?.featureFlags;
  let t12;
  if ($[14] !== contactVisibility || $[15] !== t10 || $[16] !== t11) {
    t12 = canAccessInitiativeContact(contactVisibility, {
      isAdmin: t10,
      featureFlags: t11
    });
    $[14] = contactVisibility;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const canAccessContact = t12;
  const shouldShowEnquiryForm = hasContactEmail && canAccessContact;
  const shouldShowContactDetails = hasContactEmail && !hideContactDetails && canAccessContact && (contactDetails.name || contactDetails.phone || contactDetails.website);
  const enquiryPosition = contactDetails.enquiryPosition || "bottom";
  const enquiryMessage = contactDetails.enquiryMessage || undefined;
  const enquiryTitle = contactDetails.enquiryTitle || undefined;
  const enquiryButtonLabel = contactDetails.enquiryButtonLabel || undefined;
  const contactDetailsPosition = contactDetails.contactDetailsPosition || "side";
  let t13;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = [{
      value: "general_enquiry",
      label: "General Enquiry",
      enabled: true
    }, {
      value: "partnership",
      label: "Partnership Enquiry",
      enabled: false
    }, {
      value: "custom",
      label: "Custom",
      enabled: false
    }];
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  const enquiryTypes = t13;
  let t14;
  if ($[19] !== contactDetails || $[20] !== contactDetailsPosition || $[21] !== shouldShowContactDetails) {
    t14 = shouldShowContactDetails && contactDetailsPosition === "enquiry-form-grouped" ? <ContactInfo title={null} contactDetails={contactDetails} /> : undefined;
    $[19] = contactDetails;
    $[20] = contactDetailsPosition;
    $[21] = shouldShowContactDetails;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  const enquiryFormCustomContent = t14;
  let t15;
  if ($[23] !== content.data) {
    t15 = (content.data as any)?.highlights as Highlight[] || [];
    $[23] = content.data;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const highlights = t15;
  const highlightsPosition = (content.data as any)?.highlightsPosition || "top";
  const marketingEnabled = (content.data as any)?.marketingEnabled === true;
  const marketingDetails = ((content.data as any)?.marketing || null) as Partial<MarketingDetailsValue> | null;
  const showMarketingDetails = marketingEnabled && !!marketingDetails;
  let t16;
  if ($[25] !== linkDemandTags || $[26] !== marketingDetails || $[27] !== showMarketingDetails) {
    t16 = className => showMarketingDetails ? <section className={`rounded-2xl border-2 border-primary-100 bg-white p-6 shadow-sm ${className ?? ""}`}><MarketingDetailsSnapshot demand={marketingDetails} linkTags={linkDemandTags} /></section> : null;
    $[25] = linkDemandTags;
    $[26] = marketingDetails;
    $[27] = showMarketingDetails;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const renderDemandDetails = t16;
  const t17 = content.following ?? false;
  let t18;
  if ($[29] !== content.id || $[30] !== t17) {
    t18 = <FollowButton objectId={content.id} objectType="content" following={t17} icon={Bookmark} activeIcon={BookmarkCheck} label="Save" activeLabel="Saved" showLabel={true} />;
    $[29] = content.id;
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== content || $[33] !== showEditButton) {
    t19 = showEditButton && <Link href={getContentEditUrl(content)}><Button variant="outline" size="sm" className="gap-2 cursor-pointer bg-white/90 hover:bg-white backdrop-blur-sm shadow-sm"><Edit className="h-4 w-4" />Edit</Button></Link>;
    $[32] = content;
    $[33] = showEditButton;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] !== t18 || $[36] !== t19) {
    t20 = <div className="flex items-center gap-2">{t18}{t19}</div>;
    $[35] = t18;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  const actionButtons = t20;
  const t21 = "bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden max-w-6xl mx-auto relative";
  let t22;
  if ($[38] !== content.title || $[39] !== coverImage || $[40] !== resolvedCoverImageConfig) {
    t22 = coverImage && <CoverImageBanner src={coverImage} alt={content.title || "Cover image"} config={resolvedCoverImageConfig} />;
    $[38] = content.title;
    $[39] = coverImage;
    $[40] = resolvedCoverImageConfig;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  const t23 = "p-3 md:p-6";
  const t24 = "mb-6";
  const t25 = content.title || "Untitled";
  let t26;
  if ($[42] !== t25) {
    t26 = <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-1">{t25}</h1>;
    $[42] = t25;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  const t27 = "text-sm text-gray-500 flex items-center gap-1.5 mb-3";
  let t28;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Calendar className="w-3.5 h-3.5" />;
    $[44] = t28;
  } else {
    t28 = $[44];
  }
  const t29 = formatDate(itemDate);
  let t30;
  if ($[45] !== t28 || $[46] !== t29) {
    t30 = <p className={t27}>{t28}{t29}</p>;
    $[45] = t28;
    $[46] = t29;
    $[47] = t30;
  } else {
    t30 = $[47];
  }
  let t31;
  if ($[48] !== content.type || $[49] !== typeConfig) {
    t31 = typeConfig ? <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${typeConfig.color}`}>{typeConfig.label}</span> : <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 capitalize">{content.type}</span>;
    $[48] = content.type;
    $[49] = typeConfig;
    $[50] = t31;
  } else {
    t31 = $[50];
  }
  let t32;
  if ($[51] !== content.status || $[52] !== isLive || $[53] !== statusOption?.label) {
    t32 = isLive ? null : <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"><EyeOff className="h-3 w-3" />{statusOption?.label || content.status}</span>;
    $[51] = content.status;
    $[52] = isLive;
    $[53] = statusOption?.label;
    $[54] = t32;
  } else {
    t32 = $[54];
  }
  let t33;
  if ($[55] !== content.visibility) {
    t33 = content.visibility !== "public" && <span className="text-sm text-gray-500 capitalize">{content.visibility}</span>;
    $[55] = content.visibility;
    $[56] = t33;
  } else {
    t33 = $[56];
  }
  let t34;
  if ($[57] !== t31 || $[58] !== t32 || $[59] !== t33) {
    t34 = <div className="flex flex-wrap items-center gap-3">{t31}{t32}{t33}</div>;
    $[57] = t31;
    $[58] = t32;
    $[59] = t33;
    $[60] = t34;
  } else {
    t34 = $[60];
  }
  let t35;
  if ($[61] !== t26 || $[62] !== t30 || $[63] !== t34) {
    t35 = <div className={t24}>{t26}{t30}{t34}</div>;
    $[61] = t26;
    $[62] = t30;
    $[63] = t34;
    $[64] = t35;
  } else {
    t35 = $[64];
  }
  let t36;
  if ($[65] !== actionButtons || $[66] !== allowReactions || $[67] !== content.id || $[68] !== content.title) {
    t36 = (allowReactions || true) && <div className="@container/actions mb-6"><div className="flex flex-col gap-3 @md/actions:flex-row @md/actions:items-center @md/actions:justify-between @md/actions:gap-4">{actionButtons && <div className="flex items-center shrink-0">{actionButtons}</div>}{allowReactions && <div className="min-w-0 @md/actions:flex-1"><ActivityOperations objectId={String(content.id)} objectType="content" showVotes={false} showReactions={true} dialogEnabled={true} title={content.title || "Content"} /></div>}</div></div>;
    $[65] = actionButtons;
    $[66] = allowReactions;
    $[67] = content.id;
    $[68] = content.title;
    $[69] = t36;
  } else {
    t36 = $[69];
  }
  let t37;
  if ($[70] !== content.blurb) {
    t37 = content.blurb && <p className="text-lg text-gray-600 leading-relaxed whitespace-pre-line mb-6">{content.blurb}</p>;
    $[70] = content.blurb;
    $[71] = t37;
  } else {
    t37 = $[71];
  }
  let t38;
  if ($[72] !== highlights || $[73] !== highlightsPosition) {
    t38 = highlights.length > 0 && highlightsPosition === "top" && <HighlightsDisplay highlights={highlights} position="top" />;
    $[72] = highlights;
    $[73] = highlightsPosition;
    $[74] = t38;
  } else {
    t38 = $[74];
  }
  let t39;
  if ($[75] !== highlights || $[76] !== highlightsPosition) {
    t39 = highlights.length > 0 && highlightsPosition === "side" && <div className="md:hidden"><HighlightsDisplay highlights={highlights} position="top" /></div>;
    $[75] = highlights;
    $[76] = highlightsPosition;
    $[77] = t39;
  } else {
    t39 = $[77];
  }
  let t40;
  if ($[78] !== renderDemandDetails) {
    t40 = renderDemandDetails("md:hidden mb-6");
    $[78] = renderDemandDetails;
    $[79] = t40;
  } else {
    t40 = $[79];
  }
  let t41;
  if ($[80] !== addressData || $[81] !== contactDetails || $[82] !== contactDetailsPosition || $[83] !== content || $[84] !== defaultEnquiryType || $[85] !== enquiryButtonLabel || $[86] !== enquiryFormCustomContent || $[87] !== enquiryMessage || $[88] !== enquiryPosition || $[89] !== enquiryTitle || $[90] !== enquiryTypes || $[91] !== hasAddress || $[92] !== highlights || $[93] !== highlightsPosition || $[94] !== renderDemandDetails || $[95] !== shouldShowContactDetails || $[96] !== shouldShowEnquiryForm || $[97] !== showMap) {
    t41 = content.type === "playbook" || (content.data as any)?.workflow ? <div className="space-y-8"><ContentBody content={content} />{hasAddress && showMap && <EmbeddedMap lat={addressData.geometry.location.lat} lng={addressData.geometry.location.lng} formattedAddress={addressData?.formatted_address} />}<div className="space-y-6">{highlights.length > 0 && highlightsPosition === "side" && <HighlightsDisplay highlights={highlights} position="top" />}{content.tags && content.tags.length > 0 && <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100"><ClickableTags tags={content.tags} getHref={_temp2} /></div>}{renderDemandDetails("hidden md:block")}{shouldShowContactDetails && contactDetailsPosition === "side" && <section className="bg-white rounded-lg shadow p-4"><ContactInfo contactDetails={contactDetails} /></section>}{shouldShowEnquiryForm && enquiryPosition === "side" && <div>{shouldShowContactDetails && contactDetailsPosition === "enquiry-form" && <section className="bg-white rounded-lg shadow p-4 mb-4"><ContactInfo contactDetails={contactDetails} /></section>}<EnquiryForm apiRoute="content/enquiries" resourceType="content" resourceId={content.id} enquiryTypes={enquiryTypes} className="p-2" showType={enquiryTypes.filter(_temp3).length > 1} context="content-detail" showSubject={false} sessionRequiredToSubmit={true} modalSignup={true} defaultEnquiryType={defaultEnquiryType} introMessage={enquiryMessage} submitLabel={enquiryButtonLabel} title={enquiryTitle ? <h2 className="text-xl font-semibold mb-4">{enquiryTitle}</h2> : undefined} customContent={enquiryFormCustomContent} onSuccess={_temp4} /></div>}</div></div> : <div className="grid grid-cols-1 md:grid-cols-3 gap-10"><div className="md:col-span-2 space-y-8 order-1"><ContentBody content={content} />{hasAddress && showMap && <EmbeddedMap lat={addressData.geometry.location.lat} lng={addressData.geometry.location.lng} formattedAddress={addressData?.formatted_address} />}</div><div className="space-y-6 order-2">{highlights.length > 0 && highlightsPosition === "side" && <div className="hidden md:block"><HighlightsDisplay highlights={highlights} position="side" /></div>}{content.tags && content.tags.length > 0 && <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100"><ClickableTags tags={content.tags} getHref={_temp5} /></div>}{renderDemandDetails("hidden md:block")}{shouldShowContactDetails && contactDetailsPosition === "side" && <section className="bg-white rounded-lg shadow p-4"><ContactInfo contactDetails={contactDetails} /></section>}{shouldShowEnquiryForm && enquiryPosition === "side" && <div>{shouldShowContactDetails && contactDetailsPosition === "enquiry-form" && <section className="bg-white rounded-lg shadow p-4 mb-4"><ContactInfo contactDetails={contactDetails} /></section>}<EnquiryForm apiRoute="content/enquiries" resourceType="content" resourceId={content.id} enquiryTypes={enquiryTypes} className="p-2" showType={enquiryTypes.filter(_temp6).length > 1} context="content-detail" showSubject={false} sessionRequiredToSubmit={true} modalSignup={true} defaultEnquiryType={defaultEnquiryType} introMessage={enquiryMessage} submitLabel={enquiryButtonLabel} title={enquiryTitle ? <h2 className="text-xl font-semibold mb-4">{enquiryTitle}</h2> : undefined} customContent={enquiryFormCustomContent} onSuccess={_temp7} /></div>}</div></div>;
    $[80] = addressData;
    $[81] = contactDetails;
    $[82] = contactDetailsPosition;
    $[83] = content;
    $[84] = defaultEnquiryType;
    $[85] = enquiryButtonLabel;
    $[86] = enquiryFormCustomContent;
    $[87] = enquiryMessage;
    $[88] = enquiryPosition;
    $[89] = enquiryTitle;
    $[90] = enquiryTypes;
    $[91] = hasAddress;
    $[92] = highlights;
    $[93] = highlightsPosition;
    $[94] = renderDemandDetails;
    $[95] = shouldShowContactDetails;
    $[96] = shouldShowEnquiryForm;
    $[97] = showMap;
    $[98] = t41;
  } else {
    t41 = $[98];
  }
  let t42;
  if ($[99] !== t35 || $[100] !== t36 || $[101] !== t37 || $[102] !== t38 || $[103] !== t39 || $[104] !== t40 || $[105] !== t41) {
    t42 = <div className={t23}>{t35}{t36}{t37}{t38}{t39}{t40}{t41}</div>;
    $[99] = t35;
    $[100] = t36;
    $[101] = t37;
    $[102] = t38;
    $[103] = t39;
    $[104] = t40;
    $[105] = t41;
    $[106] = t42;
  } else {
    t42 = $[106];
  }
  let t43;
  if ($[107] !== content.following || $[108] !== content.id || $[109] !== showFollowAfterContent) {
    t43 = showFollowAfterContent && <div className="p-6 md:p-10 border-t border-gray-100 flex justify-center"><FollowButton objectId={content.id} objectType="content" following={content.following ?? false} icon={Bookmark} activeIcon={BookmarkCheck} label="Save" activeLabel="Saved" showLabel={true} /></div>;
    $[107] = content.following;
    $[108] = content.id;
    $[109] = showFollowAfterContent;
    $[110] = t43;
  } else {
    t43 = $[110];
  }
  let t44;
  if ($[111] !== contactDetails || $[112] !== contactDetailsPosition || $[113] !== content.id || $[114] !== defaultEnquiryType || $[115] !== enquiryButtonLabel || $[116] !== enquiryFormCustomContent || $[117] !== enquiryMessage || $[118] !== enquiryPosition || $[119] !== enquiryTitle || $[120] !== enquiryTypes || $[121] !== shouldShowContactDetails || $[122] !== shouldShowEnquiryForm) {
    t44 = shouldShowEnquiryForm && enquiryPosition === "bottom" && <div className="p-6 md:p-10 border-t border-gray-100">{shouldShowContactDetails && contactDetailsPosition === "enquiry-form" && <section className="bg-white rounded-lg shadow p-4 mb-6"><ContactInfo contactDetails={contactDetails} /></section>}<EnquiryForm apiRoute="content/enquiries" resourceType="content" resourceId={content.id} enquiryTypes={enquiryTypes} className="p-2" showType={enquiryTypes.filter(_temp8).length > 1} context="content-detail" showSubject={false} sessionRequiredToSubmit={true} modalSignup={true} defaultEnquiryType={defaultEnquiryType} introMessage={enquiryMessage} submitLabel={enquiryButtonLabel} title={enquiryTitle ? <h2 className="text-xl font-semibold mb-4">{enquiryTitle}</h2> : undefined} customContent={enquiryFormCustomContent} onSuccess={_temp9} /></div>;
    $[111] = contactDetails;
    $[112] = contactDetailsPosition;
    $[113] = content.id;
    $[114] = defaultEnquiryType;
    $[115] = enquiryButtonLabel;
    $[116] = enquiryFormCustomContent;
    $[117] = enquiryMessage;
    $[118] = enquiryPosition;
    $[119] = enquiryTitle;
    $[120] = enquiryTypes;
    $[121] = shouldShowContactDetails;
    $[122] = shouldShowEnquiryForm;
    $[123] = t44;
  } else {
    t44 = $[123];
  }
  let t45;
  if ($[124] !== contactDetails || $[125] !== contactDetailsPosition || $[126] !== shouldShowContactDetails) {
    t45 = shouldShowContactDetails && contactDetailsPosition === "bottom" && <div className="p-6 md:p-10 border-t border-gray-100"><section className="bg-white rounded-lg shadow p-4"><ContactInfo contactDetails={contactDetails} /></section></div>;
    $[124] = contactDetails;
    $[125] = contactDetailsPosition;
    $[126] = shouldShowContactDetails;
    $[127] = t45;
  } else {
    t45 = $[127];
  }
  let t46;
  if ($[128] !== galleryDisplayMode || $[129] !== galleryItems) {
    t46 = galleryItems.length > 0 && <div className="p-6 md:p-10 border-t border-gray-100"><ImageGallery items={galleryItems} displayMode={galleryDisplayMode} className="rounded-2xl overflow-hidden" /></div>;
    $[128] = galleryDisplayMode;
    $[129] = galleryItems;
    $[130] = t46;
  } else {
    t46 = $[130];
  }
  let t47;
  if ($[131] !== allowComments || $[132] !== content.id) {
    t47 = allowComments && <div className="p-6 md:p-10 border-t border-gray-100"><ActivityFeed objectId={String(content.id)} objectType="content" canComment={true} /></div>;
    $[131] = allowComments;
    $[132] = content.id;
    $[133] = t47;
  } else {
    t47 = $[133];
  }
  let t48;
  if ($[134] !== t22 || $[135] !== t42 || $[136] !== t43 || $[137] !== t44 || $[138] !== t45 || $[139] !== t46 || $[140] !== t47) {
    t48 = <div className={t21}>{t22}{t42}{t43}{t44}{t45}{t46}{t47}</div>;
    $[134] = t22;
    $[135] = t42;
    $[136] = t43;
    $[137] = t44;
    $[138] = t45;
    $[139] = t46;
    $[140] = t47;
    $[141] = t48;
  } else {
    t48 = $[141];
  }
  return t48;
}
function _temp9() {}
function _temp8(t_1) {
  return t_1.enabled !== false;
}
function _temp7() {}
function _temp6(t_0) {
  return t_0.enabled !== false;
}
function _temp5(tag_0) {
  return `/content?tags=${encodeURIComponent(tag_0)}`;
}
function _temp4() {}
function _temp3(t) {
  return t.enabled !== false;
}
function _temp2(tag) {
  return `/content?tags=${encodeURIComponent(tag)}`;
}
function _temp(url) {
  return {
    original: url,
    thumbnail: url
  };
}